/**
 * Sign convention: income, expense and transfers are magnitudes, positive in the
 * normal case. A negative value is legal (a refund can reduce a bucket's expense
 * below zero), so nothing here rejects one. Net is income − expense.
 *
 * The Cashflow wire boundary. Money crosses as decimal strings and is parsed
 * once, here, into integer cents. A body that does not match the contract is
 * ONE `CashflowContractError` naming the offending field — never partial data.
 */
import { parseMoney } from '../../api/parseMoney'
import type { LocalDate } from '../../platform/localDate'
import type { Granularity } from './cashflowPeriod'
import { ECHO_KINDS, type EchoKind } from './cashflowScope'

type Amount = string
type TotalsWire = {
  income: Amount
  expense: Amount
  transfers_in: Amount
  transfers_out: Amount
  net: Amount
}

/** The agreed backend #497 shape. Every amount is a bare decimal string in `currency`. */
export type CashflowWire = {
  currency: string
  window: {
    start_date: string
    end_date: string
    granularity: 'day' | 'month' | 'week' | 'quarter' | 'year'
    today: string
    scope?: { kind: string; id: string | null }
  }
  totals: TotalsWire & {
    average_per_bucket: TotalsWire
    complete_bucket_count: number
    deficit_bucket_count: number
    deficit_buckets: { start_date: string; end_date: string }[]
  }
  prior: {
    start_date: string
    end_date: string
    totals: TotalsWire
    categories?: { category_id: string | null; amount: Amount }[]
    groups?: { group_id: string | null; amount: Amount }[]
  } | null
  scope: { account_count: number; shared_account_count: number }
  available: { earliest_transaction_date: string | null }
  fx: {
    source: string
    rates_as_of: string | null
    unconverted: { currency: string; transaction_count: number; amount: Amount }[]
  }
  buckets?: {
    start_date: string
    end_date: string
    complete: boolean
    income: Amount
    expense: Amount
    transfers_in: Amount
    transfers_out: Amount
    net: Amount
    /** `category_id: null` is Uncategorised. */
    top_expense_category: { category_id: string | null; amount: Amount } | null
  }[]
  categories?: {
    items: {
      category_id: string
      group_id: string | null
      amount: Amount
      share_pct: string
      series: Amount[]
    }[]
    /** `expense_transaction_count` (backend PR 509) counts the spending side only; optional, so an older body still parses. */
    uncategorised: { transaction_count: number; expense_transaction_count?: number; amount: Amount; series?: Amount[] }
    remainder?: { category_count: number; transaction_count: number; amount: Amount; share_pct: string; series: Amount[] }
  }
  groups?: { group_id: string | null; amount: Amount; share_pct: string; series?: Amount[] }[]
  merchants?: {
    items: {
      merchant_id: string
      name: string
      category_id: string | null
      amount: Amount
      transaction_count: number
      is_recurring: boolean
    }[]
    merchant_count: number
    remainder: { merchant_count: number; transaction_count: number; amount: Amount }
    /**
     * Backend 512's split of `remainder`, which stays their sum: rows with no merchant id, and the
     * merchants past the top N. Required, as the schema has them on dev and staging; a body without
     * either is a contract error (architect I2).
     */
    unassigned: { transaction_count: number; amount: Amount }
    other: { merchant_count: number; transaction_count: number; amount: Amount }
  }
  income_sources?: {
    merchant_id: string | null
    name: string
    category_id: string | null
    amount: Amount
    transaction_count: number
  }[]
  runway?: {
    cash_balance: Amount
    average_monthly_expense: Amount
    months_of_history: number
    months: string | null
    until: string | null
  } | null
}

export type Totals = {
  incomeCents: number
  expenseCents: number
  transfersInCents: number
  transfersOutCents: number
  netCents: number
}
export type Averages = Totals
export type Period = {
  start: LocalDate
  end: LocalDate
  complete: boolean
  incomeCents: number
  expenseCents: number
  transfersInCents: number
  transfersOutCents: number
  netCents: number
  /** `categoryId: null` means Uncategorised. */
  topCategory: { categoryId: string | null; amountCents: number } | null
}
export type CategorySpend = {
  categoryId: string
  groupId: string | null
  amountCents: number
  sharePct: number
  priorCents: number | null
  seriesCents: number[]
}
export type GroupSpend = {
  groupId: string | null
  amountCents: number
  sharePct: number
  priorCents: number | null
  /** `groups[].series`, one entry per bucket. Parsed for later; no screen reads it yet. Null on an older body. */
  seriesCents?: number[] | null
}
export type MerchantSpend = {
  merchantId: string
  name: string
  categoryId: string | null
  amountCents: number
  transactionCount: number
  isRecurring: boolean
}
export type IncomeSource = {
  merchantId: string | null
  name: string
  categoryId: string | null
  amountCents: number
  transactionCount: number
}
export type Runway = {
  cashCents: number
  avgMonthlyExpenseCents: number
  monthsOfHistory: number
  months: number | null
  until: LocalDate | null
}
/** The five breakdown sections of the selected period: all present or all absent. */
export type Breakdown = {
  categories: CategorySpend[]
  /**
   * `count` is both sides, as the server always sent it. `expenseCount` is the spending side
   * alone — the `total` of `/transactions?uncategorized=true&cashflow_kind=expense` — or null
   * from a server that predates it.
   */
  uncategorised: { count: number; amountCents: number; expenseCount: number | null }
  groups: GroupSpend[]
  merchants: {
    top: MerchantSpend[]
    count: number
    remainder: { count: number; transactionCount: number; amountCents: number }
    /**
     * The remainder in its two halves: `unassigned` is the "No merchant" row (rows with no merchant id),
     * `other` the merchants past the top N.
     */
    split: {
      unassigned: { transactionCount: number; amountCents: number }
      other: { count: number; transactionCount: number; amountCents: number }
    }
  }
  incomeSources: IncomeSource[]
  runway: Runway | null
}
export type Cashflow = {
  currency: string
  /**
   * `scope` is the server's echo of the drill-down it applied, in the server's kind words. It is
   * ABSENT (not null) on an unscoped body, which keeps the unscoped shape unchanged.
   */
  window: {
    start: LocalDate
    end: LocalDate
    today: LocalDate
    earliest: LocalDate | null
    scope?: { kind: EchoKind; id: string | null }
  }
  granularity: Granularity
  scope: { accounts: number; shared: number }
  totals: Totals
  averages: Averages
  completeCount: number
  deficitCount: number
  /** `totals.deficit_buckets` — the free tile names these. */
  deficitPeriods: { start: LocalDate; end: LocalDate }[]
  /** The comparison window, with its own dates: the caption is decided from them. */
  prior: (Totals & { start: LocalDate; end: LocalDate }) | null
  /** Native amounts are deliberately not surfaced: no sum, no display. */
  unconverted: { currency: string; transactionCount: number }[]
  /** Present iff the body carried `buckets` (the history call). */
  periods: Period[] | null
  /** Present iff the body carried all five breakdown sections (an entitled period call). */
  breakdown: Breakdown | null
}

export class CashflowContractError extends Error {
  readonly path: string
  constructor(path: string, message: string) {
    super(`Cashflow response broke the contract at "${path}": ${message}`)
    this.name = 'CashflowContractError'
    this.path = path
  }
}

type Rec = Record<string, unknown>

function isRec(v: unknown): v is Rec {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

const join = (base: string, key: string): string => (base === '' ? key : `${base}.${key}`)

function readRec(v: unknown, path: string): Rec {
  if (!isRec(v)) throw new CashflowContractError(path, 'expected an object')
  return v
}

function readArr(v: unknown, path: string): unknown[] {
  if (!Array.isArray(v)) throw new CashflowContractError(path, 'expected an array')
  return v
}

function readStr(v: unknown, path: string): string {
  if (typeof v !== 'string') throw new CashflowContractError(path, 'expected a string')
  return v
}

function readBool(v: unknown, path: string): boolean {
  if (typeof v !== 'boolean') throw new CashflowContractError(path, 'expected a boolean')
  return v
}

function readInt(v: unknown, path: string): number {
  if (typeof v !== 'number' || !Number.isSafeInteger(v)) {
    throw new CashflowContractError(path, 'expected an integer')
  }
  return v
}

/** An amount is a decimal STRING; a JSON number is a float and is refused. */
function readAmount(v: unknown, path: string): number {
  if (typeof v !== 'string') throw new CashflowContractError(path, 'expected a decimal string')
  try {
    return parseMoney(v)
  } catch {
    throw new CashflowContractError(path, 'not a money string')
  }
}

const DATE = /^(\d{4})-(\d{2})-(\d{2})$/

function readDate(v: unknown, path: string): LocalDate {
  const s = readStr(v, path)
  const m = DATE.exec(s)
  if (!m) throw new CashflowContractError(path, 'expected YYYY-MM-DD')
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])]
  const t = new Date(Date.UTC(y, mo - 1, d))
  if (t.getUTCFullYear() !== y || t.getUTCMonth() !== mo - 1 || t.getUTCDate() !== d) {
    throw new CashflowContractError(path, 'not a real calendar date')
  }
  return s
}

function readEnum<T extends string>(v: unknown, allowed: readonly T[], path: string): T {
  const found = allowed.find((a) => a === v)
  if (found === undefined) throw new CashflowContractError(path, `expected one of ${allowed.join(', ')}`)
  return found
}

function nullable<T>(v: unknown, read: (v: unknown) => T): T | null {
  return v === null ? null : read(v)
}

function readList<T>(v: unknown, path: string, item: (v: unknown, path: string) => T): T[] {
  return readArr(v, path).map((x, i) => item(x, `${path}[${i}]`))
}

const ONE_DECIMAL = /^\d+(\.\d)?$/

/** A decimal STRING with at most one decimal place ("12.4", "41"); a JSON number is refused. */
function readOneDecimal(v: unknown, path: string): number {
  const s = readStr(v, path)
  if (!ONE_DECIMAL.test(s)) throw new CashflowContractError(path, 'expected a whole number or one-decimal string')
  return Number(s)
}

function readTotals(v: unknown, path: string): Totals {
  const r = readRec(v, path)
  const a = (k: string) => readAmount(r[k], join(path, k))
  return {
    incomeCents: a('income'),
    expenseCents: a('expense'),
    transfersInCents: a('transfers_in'),
    transfersOutCents: a('transfers_out'),
    netCents: a('net'),
  }
}

function readBucket(v: unknown, path: string): Period {
  const r = readRec(v, path)
  const p = (k: string) => join(path, k)
  return {
    start: readDate(r.start_date, p('start_date')),
    end: readDate(r.end_date, p('end_date')),
    complete: readBool(r.complete, p('complete')),
    incomeCents: readAmount(r.income, p('income')),
    expenseCents: readAmount(r.expense, p('expense')),
    transfersInCents: readAmount(r.transfers_in, p('transfers_in')),
    transfersOutCents: readAmount(r.transfers_out, p('transfers_out')),
    netCents: readAmount(r.net, p('net')),
    topCategory: nullable(r.top_expense_category, (t) => {
      const tp = p('top_expense_category')
      const tr = readRec(t, tp)
      return {
        categoryId: nullable(tr.category_id, (x) => readStr(x, join(tp, 'category_id'))),
        amountCents: readAmount(tr.amount, join(tp, 'amount')),
      }
    }),
  }
}

type PriorLookups = {
  categories: Map<string, number>
  groups: Map<string | null, number>
}

function readPriorLookups(prior: Rec | null): PriorLookups {
  const categories = new Map<string, number>()
  const groups = new Map<string | null, number>()
  if (prior === null) return { categories, groups }
  if (prior.categories !== undefined) {
    readList(prior.categories, 'prior.categories', (x, path) => {
      const r = readRec(x, path)
      // `category_id: null` is Uncategorised (the schema allows it); the uncategorised
      // row carries no prior figure here, so it has no entry to look up.
      const id = nullable(r.category_id, (c) => readStr(c, join(path, 'category_id')))
      const amount = readAmount(r.amount, join(path, 'amount'))
      if (id !== null) categories.set(id, amount)
    })
  }
  if (prior.groups !== undefined) {
    readList(prior.groups, 'prior.groups', (x, path) => {
      const r = readRec(x, path)
      groups.set(
        nullable(r.group_id, (g) => readStr(g, join(path, 'group_id'))),
        readAmount(r.amount, join(path, 'amount')),
      )
    })
  }
  return { categories, groups }
}

function readCategory(v: unknown, path: string, prior: PriorLookups): CategorySpend {
  const r = readRec(v, path)
  const p = (k: string) => join(path, k)
  const categoryId = readStr(r.category_id, p('category_id'))
  return {
    categoryId,
    groupId: nullable(r.group_id, (x) => readStr(x, p('group_id'))),
    amountCents: readAmount(r.amount, p('amount')),
    sharePct: readOneDecimal(r.share_pct, p('share_pct')),
    priorCents: prior.categories.get(categoryId) ?? null,
    seriesCents: readList(r.series, p('series'), readAmount),
  }
}

function readGroup(v: unknown, path: string, prior: PriorLookups): GroupSpend {
  const r = readRec(v, path)
  const p = (k: string) => join(path, k)
  const groupId = nullable(r.group_id, (x) => readStr(x, p('group_id')))
  return {
    groupId,
    amountCents: readAmount(r.amount, p('amount')),
    sharePct: readOneDecimal(r.share_pct, p('share_pct')),
    priorCents: prior.groups.get(groupId) ?? null,
    seriesCents: r.series === undefined ? null : readList(r.series, p('series'), readAmount),
  }
}

function readMerchant(v: unknown, path: string): MerchantSpend {
  const r = readRec(v, path)
  const p = (k: string) => join(path, k)
  return {
    merchantId: readStr(r.merchant_id, p('merchant_id')),
    name: readStr(r.name, p('name')),
    categoryId: nullable(r.category_id, (x) => readStr(x, p('category_id'))),
    amountCents: readAmount(r.amount, p('amount')),
    transactionCount: readInt(r.transaction_count, p('transaction_count')),
    isRecurring: readBool(r.is_recurring, p('is_recurring')),
  }
}

function readIncomeSource(v: unknown, path: string): IncomeSource {
  const r = readRec(v, path)
  const p = (k: string) => join(path, k)
  return {
    merchantId: nullable(r.merchant_id, (x) => readStr(x, p('merchant_id'))),
    name: readStr(r.name, p('name')),
    categoryId: nullable(r.category_id, (x) => readStr(x, p('category_id'))),
    amountCents: readAmount(r.amount, p('amount')),
    transactionCount: readInt(r.transaction_count, p('transaction_count')),
  }
}

function readRunway(v: unknown, path: string): Runway {
  const r = readRec(v, path)
  const p = (k: string) => join(path, k)
  return {
    cashCents: readAmount(r.cash_balance, p('cash_balance')),
    avgMonthlyExpenseCents: readAmount(r.average_monthly_expense, p('average_monthly_expense')),
    monthsOfHistory: readInt(r.months_of_history, p('months_of_history')),
    months: nullable(r.months, (x) => readOneDecimal(x, p('months'))),
    until: nullable(r.until, (x) => readDate(x, p('until'))),
  }
}

/** Both halves are required: a missing one is a contract break, named at the half that is missing. */
function readSplit(mer: Rec): Breakdown['merchants']['split'] {
  const un = readRec(mer.unassigned, 'merchants.unassigned')
  const ot = readRec(mer.other, 'merchants.other')
  return {
    unassigned: {
      transactionCount: readInt(un.transaction_count, 'merchants.unassigned.transaction_count'),
      amountCents: readAmount(un.amount, 'merchants.unassigned.amount'),
    },
    other: {
      count: readInt(ot.merchant_count, 'merchants.other.merchant_count'),
      transactionCount: readInt(ot.transaction_count, 'merchants.other.transaction_count'),
      amountCents: readAmount(ot.amount, 'merchants.other.amount'),
    },
  }
}

function readPeriods(r: Rec): Period[] | null {
  return r.buckets === undefined ? null : readList(r.buckets, 'buckets', readBucket)
}

const BREAKDOWN_KEYS = ['categories', 'groups', 'merchants', 'income_sources', 'runway'] as const

function readBreakdown(r: Rec, priorRec: Rec | null): Breakdown | null {
  const present = BREAKDOWN_KEYS.filter((k) => r[k] !== undefined)
  if (present.length === 0) return null
  if (present.length !== BREAKDOWN_KEYS.length) {
    throw new CashflowContractError('breakdown', 'breakdown sections must all be present or all absent')
  }
  const prior = readPriorLookups(priorRec)
  const cats = readRec(r.categories, 'categories')
  const unc = readRec(cats.uncategorised, 'categories.uncategorised')
  const mer = readRec(r.merchants, 'merchants')
  const rem = readRec(mer.remainder, 'merchants.remainder')
  return {
    categories: readList(cats.items, 'categories.items', (x, p) => readCategory(x, p, prior)),
    uncategorised: {
      count: readInt(unc.transaction_count, 'categories.uncategorised.transaction_count'),
      amountCents: readAmount(unc.amount, 'categories.uncategorised.amount'),
      expenseCount:
        unc.expense_transaction_count === undefined
          ? null
          : readInt(unc.expense_transaction_count, 'categories.uncategorised.expense_transaction_count'),
    },
    groups: readList(r.groups, 'groups', (x, p) => readGroup(x, p, prior)),
    merchants: {
      top: readList(mer.items, 'merchants.items', readMerchant),
      count: readInt(mer.merchant_count, 'merchants.merchant_count'),
      remainder: {
        count: readInt(rem.merchant_count, 'merchants.remainder.merchant_count'),
        transactionCount: readInt(rem.transaction_count, 'merchants.remainder.transaction_count'),
        amountCents: readAmount(rem.amount, 'merchants.remainder.amount'),
      },
      split: readSplit(mer),
    },
    incomeSources: readList(r.income_sources, 'income_sources', readIncomeSource),
    runway: nullable(r.runway, (x) => readRunway(x, 'runway')),
  }
}

function readEcho(v: unknown): { kind: EchoKind; id: string | null } {
  const r = readRec(v, 'window.scope')
  return {
    kind: readEnum(r.kind, ECHO_KINDS, 'window.scope.kind'),
    id: nullable(r.id, (x) => readStr(x, 'window.scope.id')),
  }
}

function readWindow(win: Rec, avail: Rec): Cashflow['window'] {
  return {
    start: readDate(win.start_date, 'window.start_date'),
    end: readDate(win.end_date, 'window.end_date'),
    today: readDate(win.today, 'window.today'),
    earliest: nullable(avail.earliest_transaction_date, (x) => readDate(x, 'available.earliest_transaction_date')),
    ...(win.scope === undefined || win.scope === null ? {} : { scope: readEcho(win.scope) }),
  }
}

function readUnconverted(fx: Rec): Cashflow['unconverted'] {
  return readList(fx.unconverted, 'fx.unconverted', (x, p) => {
    const u = readRec(x, p)
    return {
      currency: readStr(u.currency, join(p, 'currency')),
      transactionCount: readInt(u.transaction_count, join(p, 'transaction_count')),
    }
  })
}

export function toCashflow(body: unknown): Cashflow {
  const r = readRec(body, '')
  const win = readRec(r.window, 'window')
  const scope = readRec(r.scope, 'scope')
  const avail = readRec(r.available, 'available')
  const fx = readRec(r.fx, 'fx')
  const tot = readRec(r.totals, 'totals')
  const priorRec = nullable(r.prior, (x) => readRec(x, 'prior'))
  // A free body may still carry prior.categories; only a breakdown reads them.
  const breakdown = readBreakdown(r, priorRec)
  return {
    currency: readStr(r.currency, 'currency'),
    window: readWindow(win, avail),
    // The web never asks for week, so a week body is a contract break.
    granularity: readEnum(win.granularity, ['month', 'quarter', 'year'] as const, 'window.granularity'),
    scope: {
      accounts: readInt(scope.account_count, 'scope.account_count'),
      shared: readInt(scope.shared_account_count, 'scope.shared_account_count'),
    },
    totals: readTotals(tot, 'totals'),
    averages: readTotals(tot.average_per_bucket, 'totals.average_per_bucket'),
    completeCount: readInt(tot.complete_bucket_count, 'totals.complete_bucket_count'),
    deficitCount: readInt(tot.deficit_bucket_count, 'totals.deficit_bucket_count'),
    deficitPeriods: readList(tot.deficit_buckets, 'totals.deficit_buckets', (x, p) => {
      const d = readRec(x, p)
      return { start: readDate(d.start_date, join(p, 'start_date')), end: readDate(d.end_date, join(p, 'end_date')) }
    }),
    prior:
      priorRec === null
        ? null
        : {
            ...readTotals(priorRec.totals, 'prior.totals'),
            start: readDate(priorRec.start_date, 'prior.start_date'),
            end: readDate(priorRec.end_date, 'prior.end_date'),
          },
    unconverted: readUnconverted(fx),
    periods: readPeriods(r),
    breakdown,
  }
}

/**
 * The `granularity=day` body (backend #528), read for the Overview's "When you spend" heatmap.
 * One bucket per date, empty days as "0.00". The window is the REQUESTED range (the heatmap asks for the
 * month, so it ends at the month's end, not today): every future day arrives as a "0.00" bucket with
 * `complete: false`, so `complete` and `windowEnd` do not tell you which day is today. `toCashflow` stays month/quarter/year
 * (the Cashflow screen never asks for days), so this is the day-shaped door through the same readers.
 */
export type DayBuckets = {
  currency: string
  /** The server's window end: the requested `end_date`. A window ending earlier than the month leaves the later dates bucketless. */
  windowEnd: LocalDate
  days: { date: LocalDate; expenseCents: number; complete: boolean }[]
  /** Transactions the server could not convert into `currency`: its `fx.unconverted` counts, added. */
  unconvertedCount: number
}

export function toDayBuckets(body: unknown): DayBuckets {
  const r = readRec(body, '')
  const win = readRec(r.window, 'window')
  readEnum(win.granularity, ['day'] as const, 'window.granularity')
  if (r.buckets === undefined) throw new CashflowContractError('buckets', 'a day body must carry buckets')
  const fx = readRec(r.fx, 'fx')
  const days = readList(r.buckets, 'buckets', (x, p) => {
    const b = readBucket(x, p)
    if (b.start !== b.end) throw new CashflowContractError(p, 'a day bucket must span exactly one date')
    return { date: b.start, expenseCents: b.expenseCents, complete: b.complete }
  })
  const seen = new Set<string>()
  days.forEach((d, i) => {
    if (seen.has(d.date)) throw new CashflowContractError(`buckets[${i}]`, `the date ${d.date} appears twice`)
    seen.add(d.date)
  })
  const unconverted = readList(fx.unconverted, 'fx.unconverted', (x, p) =>
    readInt(readRec(x, p).transaction_count, join(p, 'transaction_count')),
  )
  return {
    currency: readStr(r.currency, 'currency'),
    windowEnd: readDate(win.end_date, 'window.end_date'),
    days,
    unconvertedCount: unconverted.reduce((a, n) => a + n, 0),
  }
}

function alignedTo(n: number, series: number[], path: string): number[] {
  if (series.length !== n) {
    throw new CashflowContractError(path, `expected ${n} entries, one per bucket, got ${series.length}`)
  }
  return series
}

export type QueryDimension = 'category' | 'group' | 'merchant' | 'account' | 'owner' | 'tag'
const QUERY_DIMENSIONS = ['category', 'group', 'merchant', 'account', 'owner', 'tag'] as const
export type QueryCell = { ownerId: string; amountCents: number; transactionCount: number }
export type QueryRow = {
  id: string | null
  name: string
  isNone: boolean
  amountCents: number
  transactionCount: number
  sharePct: number
  seriesCents: number[]
  /** Merchant rows only, else null. */
  isRecurring: boolean | null
  /** Account rows only (nullable on the wire), else null. */
  mask: string | null
  /** Only with columns=owner, in `columns` order. */
  cells: QueryCell[] | null
}
/** What the server says it applied: lists lowercased, de-duplicated and sorted so they compare to the request. */
export type QueryFiltersEcho = {
  accountIds: string[]
  ownerIds: string[]
  categoryIds: string[]
  groupIds: string[]
  merchantIds: string[]
  tags: string[]
  uncategorised: boolean
  noMerchant: boolean
  untagged: boolean
  recurring: boolean
}
export type ReportQuery = {
  currency: string
  filters: QueryFiltersEcho
  window: Cashflow['window']
  granularity: 'month' | 'quarter'
  /** `buckets[]`: dates and completeness only. The server sends no money here. */
  periods: { start: LocalDate; end: LocalDate; complete: boolean }[]
  rowsBy: QueryDimension
  kind: 'expense' | 'income'
  total: { amountCents: number; transactionCount: number; seriesCents: number[] }
  columns: { id: string; name: string; amountCents: number; transactionCount: number }[] | null
  /** Server order. */
  rows: QueryRow[]
  remainder: {
    rowCount: number
    amountCents: number
    transactionCount: number
    sharePct: number
    seriesCents: number[]
    cells: QueryCell[] | null
  }
  /** Non-null iff rowsBy === 'tag'. */
  tagsOverlap: boolean | null
  unconverted: Cashflow['unconverted']
}

function failAt(path: string, message: string): never {
  throw new CashflowContractError(path, message)
}

function readQueryBucket(v: unknown, path: string): ReportQuery['periods'][number] {
  const b = readRec(v, path)
  return {
    start: readDate(b.start_date, join(path, 'start_date')),
    end: readDate(b.end_date, join(path, 'end_date')),
    complete: readBool(b.complete, join(path, 'complete')),
  }
}

function readCells(v: unknown, path: string, columns: ReportQuery['columns']): QueryCell[] | null {
  if (columns === null) {
    if (v !== undefined) failAt(path, 'cells without columns')
    return null
  }
  if (v === undefined) failAt(path, 'columns were asked for, so cells are required')
  const cells = readList(v, path, (x, p) => {
    const c = readRec(x, p)
    return {
      ownerId: readStr(c.owner_id, join(p, 'owner_id')),
      amountCents: readAmount(c.amount, join(p, 'amount')),
      transactionCount: readInt(c.transaction_count, join(p, 'transaction_count')),
    }
  })
  if (cells.length !== columns.length) {
    failAt(path, `expected ${columns.length} cells, one per column, got ${cells.length}`)
  }
  cells.forEach((c, j) => {
    if (c.ownerId !== columns[j]?.id) failAt(`${path}[${j}].owner_id`, 'not the column at this position')
  })
  return cells
}

function readMask(o: Rec, p: string): string | null {
  if (!('mask' in o)) failAt(join(p, 'mask'), 'an account row must carry mask (nullable)')
  return nullable(o.mask, (y) => readStr(y, join(p, 'mask')))
}

/** `filters.tax_deductible` is a reserved key the backend is removing: never read, so present or absent both parse. */
function readFilters(v: unknown): QueryFiltersEcho {
  const f = readRec(v, 'filters')
  const list = (k: string) =>
    [...new Set(readList(f[k], `filters.${k}`, (x, p) => readStr(x, p).toLowerCase()))].sort()
  const flag = (k: string) => readBool(f[k], `filters.${k}`)
  return {
    accountIds: list('account_id'),
    ownerIds: list('owner_id'),
    categoryIds: list('category_id'),
    groupIds: list('group_id'),
    merchantIds: list('merchant_id'),
    tags: list('tag'),
    uncategorised: flag('uncategorized'),
    noMerchant: flag('no_merchant'),
    untagged: flag('untagged'),
    recurring: flag('recurring'),
  }
}

/** The `GET /reports/query` body. Every figure is the server's; nothing here adds two amounts. */
export function toReportQuery(body: unknown): ReportQuery {
  const r = readRec(body, '')
  const win = readRec(r.window, 'window')
  const avail = readRec(r.available, 'available')
  const fx = readRec(r.fx, 'fx')
  const granularity = readEnum(win.granularity, ['month', 'quarter'] as const, 'window.granularity')
  const rowsBy = readEnum(r.rows_by, QUERY_DIMENSIONS, 'rows_by')
  const kind = readEnum(r.kind, ['expense', 'income'] as const, 'kind')
  const periods = readList(r.buckets, 'buckets', readQueryBucket)
  const n = periods.length
  const series = (v: unknown, path: string) => alignedTo(n, readList(v, path, readAmount), path)

  const tot = readRec(r.total, 'total')
  const total = {
    amountCents: readAmount(tot.amount, 'total.amount'),
    transactionCount: readInt(tot.transaction_count, 'total.transaction_count'),
    seriesCents: series(tot.series, 'total.series'),
  }
  const columns =
    r.columns === undefined
      ? null
      : readList(r.columns, 'columns', (x, p) => {
          const c = readRec(x, p)
          return {
            id: readStr(c.id, join(p, 'id')),
            name: readStr(c.name, join(p, 'name')),
            amountCents: readAmount(c.amount, join(p, 'amount')),
            transactionCount: readInt(c.transaction_count, join(p, 'transaction_count')),
          }
        })

  const rows = readList(r.rows, 'rows', (x, p): QueryRow => {
    const o = readRec(x, p)
    const isNone = readBool(o.is_none, join(p, 'is_none'))
    const id = nullable(o.id, (y) => readStr(y, join(p, 'id')))
    if (isNone !== (id === null)) failAt(join(p, 'id'), 'the none row, and only it, has a null id')
    return {
      id,
      name: readStr(o.name, join(p, 'name')),
      isNone,
      amountCents: readAmount(o.amount, join(p, 'amount')),
      transactionCount: readInt(o.transaction_count, join(p, 'transaction_count')),
      sharePct: readOneDecimal(o.share_pct, join(p, 'share_pct')),
      seriesCents: series(o.series, join(p, 'series')),
      isRecurring: rowsBy === 'merchant' ? readBool(o.is_recurring, join(p, 'is_recurring')) : null,
      mask: rowsBy === 'account' ? readMask(o, p) : null,
      cells: readCells(o.cells, join(p, 'cells'), columns),
    }
  })
  if (rows.filter((x) => x.isNone).length > 1) failAt('rows', 'more than one none row')
  const ids = rows.map((x) => x.id).filter((x) => x !== null)
  if (new Set(ids).size !== ids.length) failAt('rows', 'a row id appears twice')

  const rem = readRec(r.remainder, 'remainder')
  let tagsOverlap: boolean | null = null
  if (rowsBy === 'tag') {
    tagsOverlap = r.tags_overlap === undefined ? failAt('tags_overlap', 'a tag report must carry tags_overlap') : readBool(r.tags_overlap, 'tags_overlap')
  } else if (r.tags_overlap !== undefined && r.tags_overlap !== null) {
    failAt('tags_overlap', 'only a tag report carries tags_overlap')
  }
  return {
    currency: readStr(r.currency, 'currency'),
    filters: readFilters(r.filters),
    window: readWindow(win, avail),
    granularity,
    periods,
    rowsBy,
    kind,
    total,
    columns,
    rows,
    remainder: {
      rowCount: readInt(rem.row_count, 'remainder.row_count'),
      amountCents: readAmount(rem.amount, 'remainder.amount'),
      transactionCount: readInt(rem.transaction_count, 'remainder.transaction_count'),
      sharePct: readOneDecimal(rem.share_pct, 'remainder.share_pct'),
      seriesCents: series(rem.series, 'remainder.series'),
      cells: readCells(rem.cells, 'remainder.cells', columns),
    },
    tagsOverlap,
    unconverted: readUnconverted(fx),
  }
}
